import React, { useRef, useState } from "react";

import { cn } from "../../../common/u1/cn";
import { UploadIcon, CloseIcon, DocumentIcon } from "../../../../assets/ui/icons";

/* The document drop target the design uses in both dialogs. */
export const DOC_ACCEPT = ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf";
export const DOC_FORMAT_LINE = "Supported format: JPG, JPEG, PNG, PDF";

/* The CSV upload takes one kind of file and says so. Same reasoning as above:
   guidance for the picker, not a gate — the server validates the content. */
export const CSV_ACCEPT = ".csv,text/csv";
export const CSV_FORMAT_LINE = "Supported format: CSV";

export const formatBytes = (size) => {
  if (!size && size !== 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const FileDropzone = ({
  files = [],
  onChange,
  multiple = false,
  label = "Supporting Document",
  error,
  /* Both default to the document pair above; the CSV upload passes its own. */
  accept = DOC_ACCEPT,
  formatHint = DOC_FORMAT_LINE,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const take = (list) => {
    const picked = Array.from(list || []).filter(Boolean);
    if (picked.length === 0) return;
    onChange?.(multiple ? [...files, ...picked] : [picked[0]]);
  };

  const remove = (index) =>
    onChange?.(files.filter((_, i) => i !== index));

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={0}
        aria-label={multiple ? `Choose files for ${label}` : `Choose a file for ${label}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer?.files);
        }}
        className={cn(
          "flex w-full cursor-pointer flex-col items-center justify-center gap-1",
          /* The design's drop-target treatment, shared by every upload box on
             the flow: a 1.5px dashed field border on the inset ground, going
             coral on hover and while a file is over it. */
          "rounded-[13px] border-[1.5px] border-dashed px-4 py-8 text-center transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
          error || over
            ? "border-[var(--coral)] bg-[var(--coral-soft)]"
            : "border-[var(--dropzone-border)] bg-[var(--field-inset)] hover:border-[var(--coral)] hover:bg-[var(--coral-soft)]"
        )}
      >
        <span aria-hidden="true" className="text-[var(--coral)]">
          <UploadIcon size={26} />
        </span>
        <span className="text-[13px] text-[var(--ink-2)] font-sans">
          Drag &amp; drop or{" "}
          <b className="font-bold text-[var(--coral)]">Choose file</b> to upload
        </span>
        <span className="text-[11.5px] text-[var(--muted)] font-sans">
          {formatHint}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        aria-label={label}
        onChange={(e) => take(e.target.files)}
        className="sr-only"
      />

      {files.length > 0 && (
        <ul className="m-0 mt-2.5 flex list-none flex-col gap-2 p-0">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2.5 rounded-lg border border-[var(--line)] bg-[var(--bg)] px-3 py-2"
            >
              <span aria-hidden="true" className="shrink-0 text-[var(--muted-2)]">
                <DocumentIcon size={15} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold text-[var(--ink)] font-sans">
                  {file.name}
                </span>
                <span className="block text-[11px] text-[var(--muted)] font-sans">
                  {formatBytes(file.size)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${file.name}`}
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                  "text-[var(--muted)] transition-colors hover:bg-[var(--line)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
                )}
              >
                <CloseIcon size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
          {error}
        </p>
      )}
    </div>
  );
};

export default FileDropzone;
